import { useEffect, useRef } from "react";
import { File, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { resolveTaskDescriptionVisibility } from "@/lib/taskDescriptionVisibility";

type TaskDescriptionEditorVariant = "inline" | "card";

type Props = {
  value: string;
  savedValue?: string | null;
  onChange: (value: string) => void;
  onCommit: (value: string | null) => void;
  editDescription: boolean;
  onEditDescriptionChange: (editing: boolean) => void;
  variant?: TaskDescriptionEditorVariant;
  accent?: { bar: string; ink: string };
  showEmptyAction?: boolean;
  alwaysVisible?: boolean;
};

export function TaskDescriptionEditor({
  value,
  savedValue = "",
  onChange,
  onCommit,
  editDescription,
  onEditDescriptionChange,
  variant = "inline",
  accent,
  showEmptyAction = false,
  alwaysVisible = false,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const visibility =
    resolveTaskDescriptionVisibility(value, editDescription);
  const showDescription = alwaysVisible || visibility.showDescription;
  const showClearDescription = visibility.showClearDescription;

  useEffect(() => {
    if (editDescription) textareaRef.current?.focus();
  }, [editDescription]);

  function commitIfDirty() {
    onEditDescriptionChange(false);
    const trimmed = value.trim();
    if (trimmed === (savedValue ?? "")) return;
    onCommit(trimmed || null);
  }

  if (!showDescription) {
    if (!showEmptyAction) return null;

    return (
      <button
        type="button"
        onClick={() => onEditDescriptionChange(true)}
        className="flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors hover:bg-accent/20"
      >
        <File className="h-4 w-4 text-muted-foreground" />
        <span>Add description</span>
      </button>
    );
  }

  if (variant === "card") {
    return (
      <div className="ui-task-field bg-card border border-border/60 rounded-xl p-4 space-y-2">
        <div className="ui-task-field__header flex items-center justify-between">
          <div
            className="ui-task-field__label text-[11px] uppercase tracking-wider text-muted-foreground"
            style={accent ? { color: accent.ink, opacity: 0.68 } : undefined}
          >
            Details
          </div>
          <ClearButton
            visible={showClearDescription}
            color={accent?.ink}
            onClear={() => {
              onChange("");
              textareaRef.current?.focus();
            }}
          />
        </div>
        <Textarea
          ref={textareaRef}
          rows={3}
          value={value}
          placeholder="Description shown in the day list..."
          onChange={e => onChange(e.target.value)}
          onFocus={() => onEditDescriptionChange(true)}
          onBlur={commitIfDirty}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        aria-label="Task description"
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => onEditDescriptionChange(true)}
        onBlur={commitIfDirty}
        rows={1}
        className="w-full resize-none bg-transparent pr-8 text-sm leading-relaxed text-muted-foreground outline-none focus:outline-none placeholder:text-muted-foreground/30 overflow-hidden"
        placeholder="Add description..."
        onInput={e => {
          const el = e.currentTarget;
          el.style.height = "auto";
          el.style.height = el.scrollHeight + "px";
        }}
      />
      {showClearDescription && (
        <ClearButton
          visible
          onClear={() => {
            onChange("");
            textareaRef.current?.focus();
          }}
          compact
        />
      )}
    </div>
  );
}

function ClearButton({
  visible,
  color,
  onClear,
  compact = false,
}: {
  visible: boolean;
  color?: string;
  onClear: () => void;
  compact?: boolean;
}) {
  if (!visible) return null;

  return (
    <button
      type="button"
      aria-label="Clear description"
      onMouseDown={e => e.preventDefault()}
      onClick={onClear}
      style={color ? { color } : undefined}
      className={
        compact
          ? "absolute right-0 top-0 inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40"
          : "ui-task-field__clear inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      }
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}
